import {Batching, Descriptor} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/image-op-descriptor"
import {isArrayLike, isClass, isRecord, mapFields} from "@cm/utils"
import {Size2Like} from "@cm/math"
import {AnyImageOpResult} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/node/image-op-node"

export function checkSameSize(sizeA: Size2Like, sizeB: Size2Like): boolean {
    return sizeA.width === sizeB.width && sizeA.height === sizeB.height
}

export function assertSameSize(descriptorA: Size2Like, descriptorB: Size2Like): void {
    if (!checkSameSize(descriptorA, descriptorB)) {
        throw Error(`Images must have the same size: ${JSON.stringify(descriptorA)} vs ${JSON.stringify(descriptorB)}`)
    }
}

export const assertSameSizeAndBatchSize = (descriptorA: Descriptor, descriptorB: Descriptor): void => {
    assertSameSize(descriptorA, descriptorB)
    const batchSizeA = descriptorA.batching?.numPatches ?? {x: 1, y: 1}
    const batchSizeB = descriptorB.batching?.numPatches ?? {x: 1, y: 1}
    if (batchSizeA.x !== batchSizeB.x || batchSizeA.y !== batchSizeB.y) {
        throw Error(`Images must have the same batch size: ${JSON.stringify(descriptorA)} vs ${JSON.stringify(descriptorB)}`)
    }
}

export const assertSameDescriptor = (descriptorA: Descriptor, descriptorB: Descriptor): void => {
    if (!isDescriptorEqual(descriptorA, descriptorB)) {
        throw Error(`Images must have the same descriptor: ${JSON.stringify(descriptorA)} vs ${JSON.stringify(descriptorB)}`)
    }
}

export const assertNoBatching = (descriptor: Descriptor): void => {
    if (!descriptor.batching) {
        return
    }
    if (descriptor.batching.numPatches.x !== 1 || descriptor.batching.numPatches.y !== 1) {
        throw Error(`No batching is allowed here: ${JSON.stringify(descriptor)}`)
    }
}

export const hasBatching = (descriptor: Descriptor): boolean => {
    return !!descriptor.batching && (descriptor.batching.numPatches.x !== 1 || descriptor.batching.numPatches.y !== 1)
}

export const getBatching = (descriptor: Descriptor): Batching => {
    if (!descriptor.batching) {
        return {
            numPatches: {x: 1, y: 1},
            patchSize: {width: descriptor.width, height: descriptor.height},
        }
    }
    return descriptor.batching
}

export const isBatchingEqual = (a: Batching | undefined, b: Batching | undefined) => {
    return (
        a === b ||
        (a &&
            b &&
            a.numPatches.x === b.numPatches.x &&
            a.numPatches.y === b.numPatches.y &&
            a.patchSize.width === b.patchSize.width &&
            a.patchSize.height === b.patchSize.height)
    )
}

export const isDescriptorEqual = (a: Descriptor, b: Descriptor) => {
    return a === b || (a.width === b.width && a.height === b.height && a.channels === b.channels && isBatchingEqual(a.batching, b.batching))
}

export const isDescriptor = (value: unknown): value is Descriptor => {
    return typeof value === "object" && value !== null && "width" in value && "height" in value && "channels" in value
}

export const traverse = (value: unknown, fn: (value: unknown) => void) => {
    fn(value)
    if (value && !isClass(value) && (isRecord(value) || isArrayLike(value))) {
        mapFields(value, (value: unknown) => traverse(value, fn))
    }
}

export const gatherImageOpsRecursively = (value: unknown, result: AnyImageOpResult[]) => {
    const traverse = (value: unknown) => {
        if (Array.isArray(value)) {
            value.forEach(traverse)
        } else if (typeof value === "object" && value != null) {
            if (typeof value === "object" && "_imageOpKind" in value) {
                result.push(value as AnyImageOpResult) // collect imageOp
            } else if (!isClass(value)) {
                const obj = value as Record<string, unknown>
                for (const key in obj) {
                    traverse(obj[key])
                }
            }
        }
    }
    traverse(value)
}

export const addRefImageOps = (value: unknown, debugId: string) => {
    // const traverse = (value: unknown) => {
    //     if (Array.isArray(value)) {
    //         value.forEach(traverse)
    //     } else if (typeof value === "object" && value != null) {
    //         if (value instanceof ImageOpHal || value instanceof ImageOpImgProc) {
    //             value.addRef() // increment reference count for imageOps
    //         } else if (!isClass(value)) {
    //             const obj = value as Record<string, unknown>
    //             for (const key in obj) {
    //                 traverse(obj[key])
    //             }
    //         }
    //     }
    // }
    // traverse(value)
    const imageOps: AnyImageOpResult[] = []
    gatherImageOpsRecursively(value, imageOps)
    imageOps.forEach((imageOp) => imageOp.addRef(debugId))
}
